import React, { cloneElement } from 'react'
import { Helmet } from 'react-helmet-async'

import parse from 'html-react-parser'

import { useFlags } from 'shared/components/common/feature_flags/useFlags'
import { AdsManagerConversionTagsConfig } from 'src/apollo/onlineOrdering'
import { Maybe, Pixels } from 'src/apollo/sites'
import {
  CookieConsentContextType,
  useCookieConsentContext
} from 'src/public/js/cookie_consent/CookieConsentContext'
import {
  EthycaSystem,
  useSSRCookieConsentContext
} from 'src/server/SSRCookieConsentContext'
import { extractAdsPixelMeta } from 'src/shared/components/common/ads_manager/adsManagerConversions'
import useIsClient from 'src/shared/js/hooks/useIsClient'

export type PixelsMetaProps = {
  pixelsV2?: Pixels | null
  adsPixels?: string[]
  adsManagerConversionTagsConfig?:
    | AdsManagerConversionTagsConfig
    | null
    | undefined
  hasFullCustomization?: boolean | null
}

const PixelsMeta = ({
  pixelsV2,
  adsManagerConversionTagsConfig,
  hasFullCustomization
}: PixelsMetaProps) => {
  const isClient = useIsClient()
  const cookieConsentContext = useCookieConsentContext()
  const { addDisabledSystem } = useSSRCookieConsentContext()
  const { ooEthycaAds, ooAdsPixelClientRender, ooGtmConsentDefaultDeny } =
    useFlags()

  let pixelTags: JSX.Element[] = []
  let adsPixelTags: JSX.Element[] = []

  notifyEthycaOfDisabledSystems(
    pixelsV2,
    ooEthycaAds,
    addDisabledSystem,
    adsManagerConversionTagsConfig,
    hasFullCustomization
  )

  const adsPixels = extractAdsPixelMeta(
    adsManagerConversionTagsConfig,
    ooEthycaAds,
    cookieConsentContext.marketingConsent,
    cookieConsentContext.analyticsConsent,
    ooGtmConsentDefaultDeny
  )

  // isClient check ensures that we only add pixels during client-side rendering.
  // Because isClient uses useEffect, it also ensures that pixels are only added
  // after the first render - which prevents hydration mismatches.
  if (isClient) {
    pixelTags = createScriptTagsFromPixelsV2(pixelsV2, cookieConsentContext)

    if (ooAdsPixelClientRender ?? false) {
      // Wait for Ethyca consent before rendering adsPixels
      const shouldRenderAdsPixels =
        !ooEthycaAds || // If NOT using Ethyca ads feature → render immediately
        (ooEthycaAds && cookieConsentContext?.ethycaInitialized) // If using Ethyca → wait for init

      if (shouldRenderAdsPixels && adsManagerConversionTagsConfig) {
        adsPixelTags = (adsPixels || []).flatMap((pixel) =>
          createScriptTagsFromString(pixel)
        )
      }
    }
  }

  // if not running AdsClientRendering, use original ASAP logic
  // Only use old logic if flag is explicitly false, not undefined
  if (ooAdsPixelClientRender === false) {
    // adsPixels must render immediately to track conversions properly
    adsPixelTags = (adsPixels || []).flatMap((pixel) =>
      createScriptTagsFromString(pixel)
    )
  }

  // render each tag into a react element with a key.
  const pixelComponents = pixelTags.map((pixel) =>
    cloneElement(pixel, { key: `${pixel}` })
  )
  const adsPixelComponents = adsPixelTags.map((pixel) =>
    cloneElement(pixel, { key: `${pixel}` })
  )

  return (
    <Helmet>
      {pixelComponents}
      {adsPixelComponents}
    </Helmet>
  )
}

// Lets Ethyca know if certain functionality is disabled, so it can
// be excluded from the cookie banner. If Ethyca is not enabled for
// this page, values passed to addDisabledSystem are ignored.
const notifyEthycaOfDisabledSystems = (
  pixelsV2: Pixels | null | undefined,
  ooEthycaAds: boolean | undefined,
  addDisabledSystem: (system: EthycaSystem) => void,
  adsManagerConversionTagsConfig?:
    | AdsManagerConversionTagsConfig
    | null
    | undefined,
  hasFullCustomization?: boolean | null
) => {
  // Add disabled systems by default
  addDisabledSystem('pendo')
  addDisabledSystem('heap')
  addDisabledSystem('tealium')

  if (hasFullCustomization !== true) {
    addDisabledSystem('toast_guest_analytics')
    addDisabledSystem('google_tag_manager')
    addDisabledSystem('third_party_embedded_widgets')
  }

  if (!pixelsV2?.google_analytics?.content) {
    addDisabledSystem('google_analytics')
  }

  // Feature flagged logic - OO-Ethyca-Ads
  // The decision to add meta or google-ads to the disabled systems is made
  // based on whether or not we have both pixelsV2 and adsPixels sources,
  // after they have loaded, and they confirm that they are not in use
  if (ooEthycaAds ?? false) {
    // Determine if meta or google ads are in use by either the pixelsV2 or adsPixels
    const hasMeta = !!(
      hasMetaAdsInAdsPixels(adsManagerConversionTagsConfig) ||
      pixelsV2?.facebook?.content
    )
    const hasGoogleAds = !!(
      hasGoogleAdsInAdsPixels(adsManagerConversionTagsConfig) ||
      pixelsV2?.google_ads_conversion_tag?.content ||
      pixelsV2?.google_ads_conversion_tag?.scriptArg
    )

    // When adsManagerConversionTagsConfig is provided, check both sources
    // When adsManagerConversionTagsConfig is null (explicitly checked and no data),
    // still check pixelsV2 to determine if system should be disabled
    if (adsManagerConversionTagsConfig !== undefined) {
      if (!hasMeta) {
        addDisabledSystem('meta')
      }
      if (!hasGoogleAds) {
        addDisabledSystem('google_ads')
      }
    }
  } else {
    // Old logic: only check if Facebook pixel is in pixelsV2 (for backward compatibility when FF is off)
    if (!pixelsV2?.facebook?.content) {
      addDisabledSystem('meta')
    }
  }
}

const createScriptTagsFromPixelsV2 = (
  pixelsV2: Pixels | null | undefined,
  cookieConsentContext: CookieConsentContextType | null = null
): JSX.Element[] => {
  if (!pixelsV2) return []

  const scriptTags: JSX.Element[] = []

  // Helper to add script tags with proper consent handling
  const addPixel = (
    content: Maybe<string> | undefined,
    requiresEthycaConsent = false,
    ethycaConsentGranted = false
  ) => {
    if (!content || (requiresEthycaConsent && !ethycaConsentGranted)) return

    scriptTags.push(...createScriptTagsFromString(content))
  }

  // Always add these pixels (no consent required)
  const alwaysLoadPixels = [
    pixelsV2.google_tag_manager?.content,
    pixelsV2.google_ads_conversion_tag?.content,
    pixelsV2.custom?.content
  ]

  alwaysLoadPixels.forEach((content) => addPixel(content))
  addPixel(
    pixelsV2.facebook?.content,
    cookieConsentContext?.ethycaEnabled,
    cookieConsentContext?.marketingConsent
  )
  addPixel(
    pixelsV2.google_analytics?.content,
    cookieConsentContext?.ethycaEnabled,
    cookieConsentContext?.analyticsConsent
  )

  return scriptTags
}

export const createScriptTagsFromString = (
  pixelString: string
): JSX.Element[] => {
  const supportedHeadTags = ['script', 'link']
  const parsed = parse(pixelString.trim())
  if (Array.isArray(parsed)) {
    return parsed
      .filter(
        (el) => typeof el !== 'string' && supportedHeadTags.includes(el.type)
      )
      .map((el) => forceHelmetCompatibilty(el))
  } else if (typeof parsed === 'string') {
    return []
  } else {
    // parsed is JSX.Element
    if (!supportedHeadTags.includes(parsed.type)) {
      return []
    }
    return [forceHelmetCompatibilty(parsed)]
  }
}

const forceHelmetCompatibilty = (element: JSX.Element) => {
  const innerhtml = element.props.dangerouslySetInnerHTML?.__html
  const src = element.props.src
  const { dangerouslySetInnerHTML, ...otherProps } = element.props
  if (src) {
    return (
      <script {...otherProps} src={src} type='text/javascript'>
        {innerhtml}
      </script>
    )
  } else if (!src && innerhtml) {
    // Helmet requires elements without a 'src' attribute to be constructed as a JSX.IntrinsicElement, which is why we must recreate the element here.
    // This is an undocumented behavior of Helmet, and is probably a bug.
    return (
      <script {...otherProps} type='text/javascript'>
        {innerhtml}
      </script>
    )
  }
  return element
}

// Helper function to detect if adsPixels contain Google Ads
const hasGoogleAdsInAdsPixels = (
  adsManagerConversionTagsConfig?:
    | AdsManagerConversionTagsConfig
    | null
    | undefined
): boolean => {
  return !!adsManagerConversionTagsConfig?.restaurantConversionsDataList?.some(
    (conversionData) => conversionData?.googleConversionsData?.tagId
  )
}

// Helper function to detect if adsPixels contain Meta/Facebook Ads
const hasMetaAdsInAdsPixels = (
  adsManagerConversionTagsConfig?:
    | AdsManagerConversionTagsConfig
    | null
    | undefined
): boolean => {
  return !!adsManagerConversionTagsConfig?.restaurantConversionsDataList?.some(
    (conversionData) => conversionData?.metaConversionsData?.pixelId
  )
}
export default PixelsMeta
